// BLOQUE 41 — helpers de "varias facturas por una misma OC".
// Retro-compatibles: una OC anterior al Bloque 41 (solo campos escalares) se lee como
// una lista de UNA factura, y su cobertura se considera completa (igual que siempre).

export function ocInvoices(oc) {
  if (!oc) return [];
  if (Array.isArray(oc.invoices) && oc.invoices.length) return oc.invoices;
  if (oc.invoice_number) {
    return [{
      invoice_id: "legacy-1",
      invoice_number: oc.invoice_number,
      invoice_date: oc.invoice_date || "",
      invoice_total: oc.invoice_total || 0,
      fac_number: oc.fac_number || "",
      timbrado: oc.timbrado || "",
      tipo_documento: oc.tipo_documento || "Física",
      venc_timbrado: oc.venc_timbrado || "",
      items: [],
      linked_at: oc.invoice_linked_at || "",
      linked_by_name: oc.invoice_linked_by_name || "",
    }];
  }
  return [];
}

export function invoiceNumbersLabel(numbers) {
  const list = (numbers || []).filter(Boolean);
  if (!list.length) return "—";
  if (list.length === 1) return list[0];
  return `${list[0]} (+${list.length - 1})`;
}

export function ocInvoiceLabel(oc) {
  return invoiceNumbersLabel(ocInvoices(oc).map(i => i.invoice_number));
}

// Fix FE (2026-10-07): "que el sistema sí vaya imputando al ítem que corresponda según tantos
// ítems haya sido imputado por el residente" (CEN). Igual que `_repartir_por_material` del
// backend: lo facturado de un material que la OC tiene en varias líneas se reparte entre
// ellas en la proporción de la OC (la última absorbe el redondeo).
const _norm = v => String(v ?? "").trim().replace(/\s+/g, " ").toLowerCase();
function repartirPorMaterial(items, qty, amount) {
  const grupos = new Map();
  items.forEach((it, i) => {
    const k = [_norm(it.item_name || it.material_name), _norm(it.unit), _norm(it.marca_recomendada),
      _norm(it.observacion), Number(it.unit_price || 0)].join("|");
    if (!grupos.has(k)) grupos.set(k, []);
    grupos.get(k).push(i);
  });
  grupos.forEach(idxs => {
    const base = idxs.map(i => Number(items[i].quantity || 0));
    const suma = base.reduce((s, x) => s + x, 0);
    if (idxs.length < 2 || suma <= 0) return;
    const tq = idxs.reduce((s, i) => s + qty[i], 0);
    const ta = idxs.reduce((s, i) => s + amount[i], 0);
    let aq = 0, aa = 0;
    idxs.forEach((i, k) => {
      if (k === idxs.length - 1) { qty[i] = Math.round((tq - aq) * 10000) / 10000; amount[i] = Math.round((ta - aa) * 100) / 100; return; }
      qty[i] = Math.round((tq * base[k] / suma) * 10000) / 10000;
      amount[i] = Math.round((ta * base[k] / suma) * 100) / 100;
      aq += qty[i]; aa += amount[i];
    });
  });
}

// Mismo criterio que `_oc_invoice_coverage` del backend (purchases.py).
export function computeCoverage(ocItems, invoices) {
  const items = ocItems || [];
  const invs = invoices || [];
  const qty = new Array(items.length).fill(0);
  const amount = new Array(items.length).fill(0);
  const extras = [];
  invs.forEach(inv => {
    (inv.items || []).forEach(line => {
      const idx = line.item_index;
      const q = Number(line.quantity || 0);
      const sub = Number(line.subtotal || 0) || q * Number(line.unit_price || 0);
      if (Number.isInteger(idx) && idx >= 0 && idx < items.length) {
        qty[idx] += q;
        amount[idx] += sub;
      } else {
        extras.push({ invoice_number: inv.invoice_number || "", item_name: line.item_name || "", quantity: q, subtotal: sub });
      }
    });
  });
  repartirPorMaterial(items, qty, amount);   // Fix FE
  const itemsCov = items.map((it, i) => {
    const ocQty = Number(it.quantity || 0);
    const invQty = Math.round(qty[i] * 10000) / 10000;
    let status = "falta";
    if (invQty > 0.0001) {
      if (Math.abs(invQty - ocQty) <= 0.001) status = "ok";
      else if (invQty > ocQty) status = "exceso";
      else status = "parcial";
    }
    return {
      item_index: i,
      item_name: it.item_name || it.material_name || "",
      unit: it.unit || "un",
      oc_quantity: ocQty,
      invoiced_quantity: invQty,
      pending_quantity: Math.max(ocQty - invQty, 0),
      oc_subtotal: Number(it.subtotal || 0),
      invoiced_amount: amount[i],
      status,
    };
  });
  const invoicedTotal = invs.reduce((s, i) => s + Number(i.invoice_total || 0), 0);
  const ocTotal = (items || []).reduce((s, i) => s + Number(i.subtotal || 0), 0);
  const hasAssignments = invs.some(i => (i.items || []).length > 0);
  let complete;
  if (!invs.length) complete = false;
  else if (!hasAssignments || !itemsCov.length) complete = true;
  else complete = itemsCov.every(c => c.status === "ok" || c.status === "exceso");
  return {
    invoices_count: invs.length,
    invoiced_total: invoicedTotal,
    oc_total: ocTotal,
    pending_total: Math.max(ocTotal - invoicedTotal, 0),
    complete,
    has_item_assignments: hasAssignments,
    items: itemsCov,
    extras,
  };
}

// Cobertura de una OC ya guardada: usa el resumen del backend si existe.
export function ocCoverage(oc) {
  if (oc?.invoice_coverage_summary) return oc.invoice_coverage_summary;
  const cov = computeCoverage(oc?.items || [], ocInvoices(oc));
  return { ...cov, oc_total: Number(oc?.total || cov.oc_total) };
}

export function ocInvoiceComplete(oc) {
  return !!ocCoverage(oc).complete;
}

// Semáforo del AP (Bloque 18): azul = coincide, verde = de más, rojo = falta.
export const COVERAGE_STYLES = {
  ok:      "bg-blue-50 text-blue-700 border-blue-200",
  exceso:  "bg-emerald-50 text-emerald-700 border-emerald-200",
  parcial: "bg-red-50 text-red-700 border-red-200",
  falta:   "bg-red-50 text-red-700 border-red-200",
};

export const COVERAGE_LABELS = {
  ok: "Coincide",
  exceso: "Facturado de más",
  parcial: "Falta facturar",
  falta: "Sin facturar",
};
